/**
 * Per-host "quick action" chips. Each chip becomes a one-click prompt
 * pre-populated with the right context scope.
 *
 * Keeping these in one file makes it easy to A/B prompts without
 * touching the React layer.
 */

import type { OfficeHost } from '../office/bootstrap';
import type { ContextScope } from '../office/context';

export interface QuickAction {
  id: string;
  label: string;
  /** Short hint shown under the label. */
  hint?: string;
  prompt: string;
  scope: ContextScope;
}

const WORD_ACTIONS: QuickAction[] = [
  {
    id: 'summarize',
    label: 'Summarize',
    hint: 'Bullet summary of the document',
    prompt: 'Summarize the document below in clear bullet points. Highlight any decisions, risks, and open questions.',
    scope: 'document',
  },
  {
    id: 'tighten',
    label: 'Tighten writing',
    hint: 'Cut fluff, keep meaning',
    prompt: 'Rewrite the selected text to be tighter, clearer, and more direct. Preserve the original tone and meaning. Return only the rewritten text.',
    scope: 'selection',
  },
  {
    id: 'improve',
    label: 'Improve writing',
    hint: 'Grammar + flow polish',
    prompt: 'Polish the grammar, flow, and clarity of the selected text. Return only the improved text.',
    scope: 'selection',
  },
  {
    id: 'expand',
    label: 'Expand',
    hint: 'Develop the selected idea',
    prompt: 'Expand the selected text into a fuller paragraph with concrete detail and examples. Return only the new paragraph.',
    scope: 'selection',
  },
  {
    id: 'translate',
    label: 'Translate',
    hint: 'To target language',
    prompt: 'Translate the selected text to professional English. If it is already in English, translate it to Traditional Chinese instead. Return only the translation.',
    scope: 'selection',
  },
  {
    id: 'reply',
    label: 'Draft reply',
    hint: 'Reply to this email/message',
    prompt: 'The text below is an email or message. Draft a concise, professional reply. Match the tone of the original. Return only the reply body.',
    scope: 'document',
  },
];

const EXCEL_ACTIONS: QuickAction[] = [
  {
    id: 'explain-formula',
    label: 'Explain formula',
    hint: 'Plain-English breakdown',
    prompt: 'Explain what the selected cell\'s formula does in plain English. Walk through it argument by argument.',
    scope: 'selection',
  },
  {
    id: 'generate-formula',
    label: 'Generate formula',
    hint: 'Tell me what you want',
    prompt: 'Write the Excel formula that achieves what I describe in the next sentence. Return only the formula starting with "=" — no explanation, no surrounding text.',
    scope: 'selection',
  },
  {
    id: 'analyze',
    label: 'Analyze range',
    hint: 'Trends, totals, anomalies',
    prompt: 'Analyze the data range below. Identify totals, trends, outliers, and anything notable. Use short bullet points.',
    scope: 'selection',
  },
  {
    id: 'clean',
    label: 'Clean column',
    hint: 'Standardize values',
    prompt: 'Standardize and clean the selected column values (trim, casing, consistent format). Return the cleaned values as a tab-separated column with one value per line — no headers, no commentary.',
    scope: 'selection',
  },
  {
    id: 'summarize-sheet',
    label: 'Summarize sheet',
    hint: 'Whole-sheet snapshot',
    prompt: 'Summarize the contents and structure of the worksheet below. Note key columns, what the data appears to represent, and any data-quality issues.',
    scope: 'document',
  },
];

const PPT_ACTIONS: QuickAction[] = [
  {
    id: 'speaker-notes',
    label: 'Speaker notes',
    hint: 'For the current slide',
    prompt: 'Write natural, conversational speaker notes for the slide content below. Aim for ~45 seconds of speaking time.',
    scope: 'selection',
  },
  {
    id: 'rewrite-slide',
    label: 'Rewrite slide',
    hint: 'Punchier headlines',
    prompt: 'Rewrite the slide text below as a punchy executive-ready slide: a clear headline, then 3–5 short bullets. Return as plain text.',
    scope: 'selection',
  },
  {
    id: 'outline',
    label: 'Outline a deck',
    hint: 'From the current slide',
    prompt: 'Using the current slide as the topic seed, propose a 6-slide deck outline. For each slide give a title and 2–3 bullets.',
    scope: 'selection',
  },
  {
    id: 'visuals',
    label: 'Suggest visuals',
    hint: 'Diagram & chart ideas',
    prompt: 'Suggest 3 visual treatments (chart, diagram, layout) that would strengthen the slide content below. Be specific about what data or shapes would appear.',
    scope: 'selection',
  },
];

const TEAMS_ACTIONS: QuickAction[] = [
  {
    id: 'brainstorm',
    label: 'Brainstorm',
    hint: 'Spark new ideas',
    prompt: 'Help me brainstorm fresh ideas on the topic I describe next. Give 6 distinct angles, each as a 1-line headline followed by a one-sentence rationale.',
    scope: 'none',
  },
  {
    id: 'meeting-agenda',
    label: 'Meeting agenda',
    hint: 'Draft a structured agenda',
    prompt: 'Draft a meeting agenda for the topic I describe next. Include suggested time blocks, owners, and 2 desired outcomes.',
    scope: 'none',
  },
  {
    id: 'reply',
    label: 'Draft reply',
    hint: 'Reply to a chat / email',
    prompt: 'Draft a concise, professional reply for the message I paste next. Match its tone. Return only the reply body.',
    scope: 'none',
  },
  {
    id: 'summarize-thread',
    label: 'Summarize thread',
    hint: 'Distill a conversation',
    prompt: 'Summarize the conversation I paste next. Pull out: decisions made, action items (with owners), and open questions.',
    scope: 'none',
  },
  {
    id: 'explain',
    label: 'Explain like I\'m new',
    hint: 'Plain-English explainer',
    prompt: 'Explain the concept I describe next in plain English. Assume the reader is a smart non-expert. Keep it under 200 words.',
    scope: 'none',
  },
  {
    id: 'translate',
    label: 'Translate',
    hint: 'EN ↔ Traditional Chinese',
    prompt: 'Translate the text I paste next. If it is in English, translate to Traditional Chinese. Otherwise translate to professional English. Return only the translation.',
    scope: 'none',
  },
];

const MEET_ACTIONS: QuickAction[] = [
  {
    id: 'agenda',
    label: 'Meeting agenda',
    hint: 'Structured agenda with time blocks',
    prompt: 'Draft a meeting agenda for the topic I describe next. Include time-boxed sections, owners, and 2 desired outcomes.',
    scope: 'none',
  },
  {
    id: 'icebreaker',
    label: 'Icebreaker',
    hint: 'Open the meeting warmly',
    prompt: 'Suggest 3 quick icebreaker questions appropriate for the meeting I describe next. Keep them inclusive and ~30 seconds each.',
    scope: 'none',
  },
  {
    id: 'summarize-notes',
    label: 'Summarize notes',
    hint: 'Distill raw call notes',
    prompt: 'Summarize the meeting notes I paste next. Pull out: decisions made, action items (with owners and due dates), and open questions.',
    scope: 'none',
  },
  {
    id: 'follow-up',
    label: 'Draft follow-up',
    hint: 'Post-meeting email',
    prompt: 'Draft a concise follow-up email based on the meeting summary I paste next. Include a thanks line, the agreed actions, and propose a next check-in.',
    scope: 'none',
  },
  {
    id: 'translate',
    label: 'Translate',
    hint: 'EN ↔ Traditional Chinese',
    prompt: 'Translate the text I paste next. If it is in English, translate to Traditional Chinese. Otherwise translate to professional English. Return only the translation.',
    scope: 'none',
  },
];

export function quickActionsFor(host: OfficeHost): QuickAction[] {
  switch (host) {
    case 'Word':       return WORD_ACTIONS;
    case 'Excel':      return EXCEL_ACTIONS;
    case 'PowerPoint': return PPT_ACTIONS;
    case 'Teams':      return TEAMS_ACTIONS;
    case 'Meet':       return MEET_ACTIONS;
    default:           return [];
  }
}
